import { FeatureEvaluationContext } from './context';
import {
  getFeature,
  getOverrideValue,
  parseFeatureOverride,
} from './evaluation-helpers';
import { FeatureEvaluationOptions } from './evaluation.types';
import { FEATURE_REGISTRY } from './registry';
import {
  FeatureKey,
  FeatureOverrides,
  FeatureRegistry,
} from './types';

export enum FeatureResolutionSource {
  OVERRIDE = 'override',
  REGISTRY_DEFAULT = 'registry_default',
  /** Reserved for future resolver implementations. */
  SUBSCRIPTION = 'subscription',
  CREATOR_PLAN = 'creator_plan',
  BETA_PROGRAM = 'beta_program',
  INTERNAL_ACCESS = 'internal_access',
  PLATFORM = 'platform',
  CUSTOM = 'custom',
}

export interface FeatureEvaluationResult {
  key: FeatureKey;
  enabled: boolean;
  source: FeatureResolutionSource;
  /** Human-readable detail for logs, admin tools, and debugging. */
  reason?: string;
}

export interface FeatureResolverContext {
  key: FeatureKey;
  registry: FeatureRegistry;
  overrides?: FeatureOverrides;
  evaluationContext?: FeatureEvaluationContext;
  baseResult: FeatureEvaluationResult;
}

/**
 * Pluggable resolver in the evaluation pipeline.
 * Return null to defer to the next resolver.
 */
export interface FeatureResolver {
  readonly name: string;
  resolve(context: FeatureResolverContext): FeatureEvaluationResult | null;
}

export interface FeatureEvaluatorOptions extends FeatureEvaluationOptions {
  resolvers?: FeatureResolver[];
}

function resolveBaseFeatureState(
  key: FeatureKey,
  options: FeatureEvaluationOptions,
): FeatureEvaluationResult {
  const registry = options.registry ?? FEATURE_REGISTRY;
  const feature = getFeature(key, registry);
  const override = getOverrideValue(key, options.overrides);

  if (override !== undefined) {
    return {
      key,
      enabled: parseFeatureOverride(override, feature.defaultState),
      source: FeatureResolutionSource.OVERRIDE,
      reason: 'Environment or caller override applied',
    };
  }

  return {
    key,
    enabled: feature.defaultState,
    source: FeatureResolutionSource.REGISTRY_DEFAULT,
    reason: 'Registry default state',
  };
}

function runResolverPipeline(
  resolverContext: FeatureResolverContext,
  resolvers: FeatureResolver[],
): FeatureEvaluationResult {
  let current = resolverContext.baseResult;

  for (const resolver of resolvers) {
    const next = resolver.resolve({ ...resolverContext, baseResult: current });
    if (next) {
      current = next;
    }
  }

  return current;
}

/**
 * Evaluates a feature through the base registry layer and optional resolver pipeline.
 * Default behavior matches pre-extensibility isFeatureEnabled().
 */
export function evaluateFeature(
  key: FeatureKey,
  options: FeatureEvaluatorOptions = {},
): FeatureEvaluationResult {
  const registry = options.registry ?? FEATURE_REGISTRY;
  const baseResult = resolveBaseFeatureState(key, options);
  const resolvers = options.resolvers ?? [];

  if (resolvers.length === 0) {
    return baseResult;
  }

  return runResolverPipeline(
    {
      key,
      registry,
      overrides: options.overrides,
      evaluationContext: options.evaluationContext,
      baseResult,
    },
    resolvers,
  );
}

export function evaluateFeatureMap(
  options: FeatureEvaluatorOptions = {},
): Readonly<Record<FeatureKey, FeatureEvaluationResult>> {
  return Object.values(FeatureKey).reduce<Record<FeatureKey, FeatureEvaluationResult>>(
    (results, key) => {
      results[key] = evaluateFeature(key, options);
      return results;
    },
    {} as Record<FeatureKey, FeatureEvaluationResult>,
  );
}

/**
 * Factory for applications that will register custom resolvers (billing, beta, etc.).
 */
export function createFeatureEvaluator(resolvers: FeatureResolver[] = []) {
  return {
    evaluate: (key: FeatureKey, options: FeatureEvaluationOptions = {}) =>
      evaluateFeature(key, { ...options, resolvers }),
    evaluateAll: (options: FeatureEvaluationOptions = {}) =>
      evaluateFeatureMap({ ...options, resolvers }),
  };
}

/** Default evaluator with no contextual resolvers — current production behavior. */
export const defaultFeatureEvaluator = createFeatureEvaluator([]);
